import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as t, radius } from '@/theme/tokens';

/**
 * Marks a region of the UI as a demo: shipped, interactive, but not backed by
 * anything real yet.
 *
 * Screenshots outlive the conversation that produced them. A panel someone was
 * told is illustrative becomes, three weeks later, a slide asserting the
 * feature exists — so the caveat has to live in the pixels rather than in
 * whoever is presenting.
 *
 * Deliberately unconditional: no env check, no role gate. A marker that
 * disappears in production is absent exactly when a real customer is looking,
 * which is the case that matters most.
 */
interface DemoFeatureProps {
  children: React.ReactNode;
  /** Pill text. Keep it to a word or two — it sits on the border. */
  label?: string;
  /**
   * What is not real, for screen readers and for anyone who needs more than
   * the pill. Not shown; the visual signal is the outline.
   */
  description?: string;
  sx?: SxProps<Theme>;
}

/**
 * What a `beta` pill means on a feature, said once.
 *
 * Deliberately NOT the sidebar's BETA_GROUP_HINT, which reads "still being
 * built — not ready for testing yet". That copy is right for a half-built page
 * and wrong for a finished feature that is merely new: telling people not to
 * test something you want tested is worse than saying nothing. This is the
 * `beta` tone's own definition — real, working, early — in a sentence.
 */
export const BETA_FEATURE_HINT =
  'Beta — new and still settling. Anything you create now is stored and keeps working.';

/**
 * The standalone pill, for regions too small to frame — a nav entry, a page
 * heading — and for claims that aren't "this data is fake".
 *
 * The tone is the claim, and they are genuinely different:
 *  - `demo`  amber: attention, not failure. The feature is a stand-in.
 *  - `beta`  info blue: the feature is real and works; it is early.
 * Red is deliberately unused — it already means blocked and fault here.
 */
export function FeatureBadge({
  label,
  tone = 'demo',
  surface = 'default',
  sx,
}: {
  label: string;
  tone?: 'demo' | 'beta';
  /**
   * The ground it sits on, which is a separate question from what it claims.
   * The sidebar is navy, and orange when the row is selected — a pale blue or
   * amber chip fights both. On `dark` the badge goes translucent white and
   * borrows whatever is behind it, so one component works on every surface.
   */
  surface?: 'default' | 'dark';
  sx?: SxProps<Theme>;
}) {
  const palette =
    surface === 'dark'
      ? {
          fg: 'inherit',
          bg: 'rgba(255,255,255,0.18)',
          border: 'rgba(255,255,255,0.38)',
        }
      : tone === 'beta'
        ? { fg: colors.info.dark, bg: colors.info.light, border: colors.info.main }
        : { fg: colors.warning.dark, bg: colors.warning.light, border: colors.warning.main };
  return (
    <Typography
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: palette.bg,
        color: palette.fg,
        border: '1px solid',
        borderColor: palette.border,
        borderRadius: `${radius.pill}px`,
        px: 0.75,
        fontSize: 10,
        fontWeight: t.fontWeight.semibold,
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        lineHeight: 1.7,
        whiteSpace: 'nowrap',
        flex: 'none',
        ...sx,
      }}
    >
      {label}
    </Typography>
  );
}

export function DemoFeature({
  children,
  label = 'Demo',
  description = 'This feature is a demo. The data it shows is illustrative, not live.',
  sx,
}: DemoFeatureProps) {
  return (
    <Box
      role="note"
      aria-label={`${label}: ${description}`}
      sx={{
        position: 'relative',
        // Dashed, so it never reads as a real container's edge, and inset from
        // the content so wrapping a card doesn't collide with the card's own
        // border.
        border: '1px dashed',
        borderColor: colors.warning.main,
        borderRadius: `${radius.lg}px`,
        p: 0.75,
        // Grid and flex parents hand this box the slot the child used to have,
        // so it has to fill that slot rather than shrink to content.
        height: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Typography
        component="span"
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          right: 12,
          transform: 'translateY(-50%)',
          // Sits ON the dashed line, so the pill reads as part of the frame
          // rather than as a badge floating over the content.
          bgcolor: colors.status.pending.bg,
          color: colors.warning.dark,
          border: '1px solid',
          borderColor: colors.warning.main,
          borderRadius: `${radius.pill}px`,
          px: 0.875,
          py: 0.125,
          fontSize: t.fontSize.xs,
          fontWeight: t.fontWeight.semibold,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          lineHeight: 1.6,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>

      {/* The child keeps its own layout; this only carries the frame. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    </Box>
  );
}

export default DemoFeature;
